import React, { useMemo } from "react";
import { usePuellMultipleChart } from "@/hooks/coinglass/usePuellMultipleChart";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import type { EChartsOption } from "echarts";

interface PuellMultipleChartProps {
  startDate?: number;
  endDate?: number;
  limit?: number;
  height?: string | number;
}

export const PuellMultipleChart: React.FC<PuellMultipleChartProps> = ({
  startDate,
  endDate,
  limit,
  height = 500,
}) => {
  const { data, isLoading, isError, error } = usePuellMultipleChart({
    start_timestamp: startDate,
    end_timestamp: endDate,
    limit,
  });

  const option = useMemo<EChartsOption | null>(() => {
    if (!data?.data) return null;

    const { xAxis, series } = data.data;

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        backgroundColor: "rgba(20, 20, 30, 0.9)",
        borderColor: "#333",
        textStyle: { color: "#fff" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          
          const p = params;
          const date = p[0].axisValue;
          const puell = p[0].data;
          const price = p[1]?.data;

          let zone = '<span style="color: #9ca3af;">Normal Zone</span>';
          if (puell > 4) zone = '<span style="color: #ef4444; font-weight: bold;">🔴 Overvalued (Top Signal)</span>';
          else if (puell < 0.5) zone = '<span style="color: #10b981; font-weight: bold;">🟢 Accumulation Zone</span>';

          return `
            <div style="padding: 8px; min-width: 200px;">
              <div style="font-weight: bold; margin-bottom: 8px; color: #9ca3af;">${date}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #d1d5db;">Puell Multiple:</span>
                <span style="font-weight: bold; color: #10b981;">${typeof puell === 'number' ? puell.toFixed(4) : puell}</span>
              </div>
              ${price !== undefined ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span style="color: #d1d5db;">BTC Price:</span>
                <span style="font-weight: bold; color: #fbbf24;">$${typeof price === 'number' ? price.toLocaleString() : price}</span>
              </div>
              ` : ''}
              <div style="margin-top: 8px; border-top: 1px solid #374151; padding-top: 6px;">
                ${zone}
              </div>
            </div>
          `;
        }
      },
      legend: {
        data: ["Puell Multiple", "BTC Price"],
        textStyle: { color: "#9ca3af" },
        top: 0,
        left: "center",
        icon: "circle",
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "12%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxis,
        axisLabel: {
          color: "#9ca3af",
          hideOverlap: true,
          interval: 'auto',
          formatter: (value: string) => {
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          },
        },
        axisLine: { lineStyle: { color: "#374151" } },
      },
      yAxis: [
        {
          type: "value",
          name: "Puell Multiple",
          position: "left",
          splitLine: { lineStyle: { color: "#1f2937" } },
          axisLabel: { color: "#9ca3af", fontWeight: "normal" },
          axisLine: { lineStyle: { color: "#374151" } },
        },
        {
          type: "log", // Logarithmic scale
          logBase: 10,
          name: "BTC Price (USD)",
          position: "right",
          splitLine: { show: false },
          axisLabel: {
            color: "#9ca3af",
            fontWeight: "normal",
            formatter: (value: number) => {
              if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
              return `$${value}`;
            },
          },
          axisLine: { lineStyle: { color: "#374151" } },
        },
      ],
      series: series.map((s, index) => ({
        ...s,
        // Ensure correct yAxisIndex and styling
        yAxisIndex: index === 0 ? 0 : 1,
        itemStyle: { color: index === 0 ? "#10b981" : "#fbbf24" },
        lineStyle: { width: 2 },
        symbol: "none",
        // Add markLines for Puell Multiple if it's the first series
        markLine: index === 0 ? {
          silent: true,
          symbol: "none",
          data: [
            {
              yAxis: 4.0,
              label: { 
                formatter: "Overvalued (4.0)",
                position: "end",
                color: "#ef4444"
              },
              lineStyle: { color: "rgba(239, 68, 68, 0.6)", type: "dashed" }
            },
            {
              yAxis: 0.5,
              label: { 
                formatter: "Accumulation (0.5)",
                position: "end",
                color: "#10b981"
              },
              lineStyle: { color: "rgba(16, 185, 129, 0.6)", type: "dashed" }
            }
          ]
        } : undefined
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      })) as any,
      dataZoom: [
        { type: "inside", start: 70, end: 100 },
        {
          type: "slider",
          show: true,
          bottom: 30,
          height: 20,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af" },
          brushSelect: false,
        },
      ],
    };
  }, [data]);

  return (
    <EChartsRenderer
      chartOption={option || {}}
      height={height}
      isLoading={isLoading}
      error={isError ? (error as Error).message : null}
      hasData={!!data?.data}
      containerClassName="w-full h-full"
    />
  );
};
